/**
 * TURNING WHAT SOMEBODY TYPED INTO A KEY.
 *
 * `0105` constrains `capability_model.model_key` to lower-case letters, digits and hyphens,
 * 2-64 characters, no leading or trailing hyphen — the same shape `capability_template_key`
 * already uses, so the two read alike.
 *
 * ── WHY THIS EXISTS ───────────────────────────────────────────────────────────────────
 *
 * The first cut asked for the key by hand and REFUSED anything that did not already match,
 * which is how the owner met it, 2026-10-07: *"I tried to create capability model from
 * template and upon Create Model, I am getting A key is 2 to 64 characters, using lower-case
 * letters, digits and hyphens … error message"*.
 *
 * That is a bad trade dressed up as validation. "FY27 SCM" is a perfectly clear answer to
 * "what is this model's key?", and the machine knows exactly what it meant. Refusing it makes
 * the person do an encoding job a function can do, and the message — correct, specific, even
 * carrying an example — is still a wall rather than a help.
 *
 * So the rule is: NORMALISE, DO NOT REFUSE. The only inputs that still fail are the ones with
 * nothing in them to normalise.
 *
 * ── TWO FUNCTIONS, BECAUSE TYPING AND SUBMITTING ARE DIFFERENT MOMENTS ────────────────
 *
 * A single normaliser applied on every keystroke cannot work: strip the trailing hyphen as the
 * user types and "fy27-" collapses to "fy27", so the hyphen can never be followed by anything
 * and "fy27-scm" is unreachable. The edges are therefore tidied only when the value is USED.
 */

/** 2-64 characters: lower-case letters, digits, single hyphens between them. `0105`'s CHECK. */
export const MODEL_KEY_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

export const MODEL_KEY_MIN = 2;
export const MODEL_KEY_MAX = 64;

/**
 * As the user types. Lower-cases, turns whitespace and underscores into hyphens, drops anything
 * else, and collapses runs — but LEAVES THE EDGES ALONE so a hyphen can still be typed and then
 * typed after.
 */
export function draftKey(text: string): string {
  return text
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-{2,}/g, '-')
    .slice(0, MODEL_KEY_MAX);
}

/** On use. `draftKey` plus the edges, which is the form the server and the CHECK will see. */
export function finishKey(text: string): string {
  return draftKey(text).replace(/^-+|-+$/g, '');
}

/**
 * A key SUGGESTED from a name, for seeding a field rather than for deciding anything.
 *
 * Seeding matters as much as normalising did: an empty key field asks somebody to invent an
 * identifier before they have been told what one looks like, and the example in the hint is the
 * only clue. A field that arrives already holding `sap-ecc-supply-chain` has answered the
 * question by showing it.
 *
 * It is a STARTING POINT, not a derivation — every character stays editable, and a model whose
 * key should not follow its name just gets typed over. Returns '' when the name yields nothing
 * usable, which leaves the field empty rather than inventing something unrelated.
 */
export function suggestKeyFromName(name: string): string {
  const key = finishKey(name);
  return key.length >= MODEL_KEY_MIN ? key : '';
}

/** `null` when the key is usable, otherwise the sentence to show. */
export function describeKeyProblem(text: string): string | null {
  const key = finishKey(text);
  if (key === '') return 'Key is required.';
  if (key.length < MODEL_KEY_MIN) {
    return `A key needs at least ${MODEL_KEY_MIN} characters — letters or digits.`;
  }
  /*
   * Unreachable in practice: `finishKey` cannot return a non-empty string that fails the
   * pattern. Kept so the client and `0105`'s CHECK can never silently disagree if either side
   * is edited later — this is the line that would start failing, loudly, rather than the
   * database rejecting a write nobody expected it to.
   */
  if (!MODEL_KEY_PATTERN.test(key)) {
    return 'A key uses lower-case letters, digits and hyphens — for example fy27-scm.';
  }
  return null;
}
